"use client";

import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Универсальный confirm-dialog для удаления (Saleor-style).
 * Заменяет window.confirm() во всех admin местах.
 *
 * Usage:
 * <DeleteConfirmDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onConfirm={async () => { await mutate(); setOpen(false); }}
 *   itemName="Категория 'Люстры'"
 *   description="Содержит 5 товаров. Они будут перемещены в parent-категорию."
 * />
 */
export function DeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Удалить?",
  itemName,
  description,
  confirmLabel = "Удалить",
  cancelLabel = "Отмена",
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  title?: string;
  itemName?: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}) {
  const [pending, setPending] = useState(false);

  async function handleConfirm() {
    setPending(true);
    try {
      await onConfirm();
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !pending && !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-error/10 text-error">
              <AlertTriangle className="h-4 w-4" />
            </div>
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 text-sm">
          {itemName && (
            <p className="text-foreground">
              Вы уверены что хотите удалить{" "}
              <strong className="font-semibold">«{itemName}»</strong>?
            </p>
          )}
          {description && (
            <div className="text-muted-foreground">{description}</div>
          )}
          <p className="text-xs text-muted-foreground">
            Это действие нельзя отменить.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={pending}
            className="gap-1.5"
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
